import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { clone, remove } from 'lodash-es';
import { v4 } from 'uuid';
import { Box, Button, Checkbox, ClickAwayListener, Grid, IconButton, Paper, Popper, Slider, Stack, Toolbar, Typography, } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { navStore } from '@mcn-platform/models';
import Renderer, { getData, } from '../Renderer';
import BackgroundImage from './BackgroundImage';
import CloseIcon from '../../portal/icons/CloseIcon';
import PlaceHolder from '../../portal/common/PlaceHolder';
/**
 * Updates the column to the new value.  Adustments are made to the next column to the right to
 * ensure the total adds up to 12.  If the next column can't accomodate the next column is
 * used.
 */
const updateColumns = (columns, breakpoint, columnId, newValue) => {
    // special case is if we have 12 columns - we can't change the number
    // - should be 1 for each column
    if (columns.length === 12) {
        return columns;
    }
    const newColumns = clone(columns);
    const columnIndex = newColumns.findIndex((config) => (config.id === columnId));
    const columnConfig = clone(newColumns[columnIndex]);
    // update the column to the new value
    columnConfig[breakpoint] = newValue;
    newColumns[columnIndex] = columnConfig;
    return newColumns;
};
const breakpoints = [
    { key: 'xs', label: 'xs', name: 'Xtra Small (<600px)' },
    { key: 'sm', label: 'sm', name: 'Small (<900px)' },
    { key: 'md', label: 'md', name: 'Medium (<1200px)' },
    { key: 'lg', label: 'lg', name: 'Large (<1536px)' },
    { key: 'xl', label: 'xl', name: 'Xtra Large (>1536)' },
];
const marks = [
    { value: 1, label: '8⅓' },
    { value: 2, label: '16⅔' },
    { value: 3, label: '25' },
    { value: 4, label: '33⅓' },
    { value: 5, label: '41⅔' },
    { value: 6, label: '50' },
    { value: 7, label: '58⅓' },
    { value: 8, label: '66⅔' },
    { value: 9, label: '75' },
    { value: 10, label: '83⅓' },
    { value: 11, label: '91⅔' },
    { value: 12, label: '100' },
];
/**
 * Gets the inherited value for the breakpoint - its the
 * nearest smaller breakpoint that has a value.  XS is
 * required to always have a value.
 */
const getInheritedValue = (columnConfig, breakpointIndex) => {
    let currentIndex = breakpointIndex;
    while (currentIndex >= 0) {
        const value = columnConfig[breakpoints[currentIndex].key];
        if (value) {
            return value;
        }
        currentIndex -= 1;
    }
    return 1;
};
/**
 * Gets the inherited value for the breakpoint - its the
 * nearest smaller breakpoint that has a value.  XS is
 * required to always have a value.
 */
const summarizeColumn = (columnConfig) => (breakpoints.map((bp, index) => {
    const value = columnConfig[bp.key];
    if (!value) {
        return '';
    }
    return `${index > 0 ? '<' : ''} ${bp.key} ${marks[value - 1].label} `;
}));
const ColumnField = ({ breakpoint, index, label, columnConfig, setValue,
// eslint-disable-next-line arrow-body-style
 }) => {
    const value = breakpoint in columnConfig ? columnConfig[breakpoint] : undefined;
    return (_jsxs(Stack, { direction: "row", spacing: 1, alignItems: "center", children: [_jsx(Typography, { sx: { width: 24 }, children: label }), _jsx(Checkbox, { disabled: index === 0, checked: index === 0 ? true : Boolean(value), onChange: () => {
                    setValue(breakpoint, columnConfig.id, value ? null : getInheritedValue(columnConfig, index));
                } }), _jsx(Slider, { disabled: !value, marks: marks, step: null, min: 0, max: 12, sx: {
                    height: 16,
                    width: 240,
                }, value: value || getInheritedValue(columnConfig, index), onChange: (event, newValue) => {
                    setValue(breakpoint, columnConfig.id, newValue);
                } })] }));
};
const ColumnConfigEditor = ({ columnConfig, setValue, deleteColumn }) => {
    const [anchorEl, setAnchorEl] = React.useState(null);
    // console.log('Column config', columnConfig);
    const handleClick = (event) => {
        setAnchorEl(anchorEl ? null : event.currentTarget);
    };
    const handleClickAway = () => {
        setAnchorEl(null);
    };
    const open = Boolean(anchorEl);
    const id = open ? `popper-${columnConfig.id}` : undefined;
    return (_jsxs(Toolbar, { variant: "dense", sx: {
            marginX: {
                xs: 0,
            },
            paddingX: {
                xs: 1,
            },
            borderRadius: '2px',
            backgroundColor: 'secondary.light',
            minHeight: 'auto',
            opacity: 0.9,
        }, children: [_jsx(Button, { onClick: handleClick, endIcon: _jsx(KeyboardArrowDownIcon, {}), size: "small", variant: "outlined", children: summarizeColumn(columnConfig) }), _jsx(IconButton, { onClick: (event) => {
                    event.stopPropagation();
                    deleteColumn(columnConfig.id);
                    // editData.actions.deleteItem(id);
                }, children: _jsx(DeleteIcon, {}) }), _jsx(Popper, { id: id, open: open, anchorEl: anchorEl, placement: "top-start", style: { zIndex: 9999 }, children: _jsx(ClickAwayListener, { onClickAway: handleClickAway, children: _jsxs(Paper, { elevation: 1, sx: { padding: 2 }, children: [_jsx(Box, { m: -2, display: "flex", justifyContent: "flex-end", alignItems: "flex-end", sx: {}, children: _jsx(IconButton, { onClick: handleClickAway, children: _jsx(CloseIcon, {}) }) }), breakpoints.map((bp, index) => (_jsx(ColumnField, { breakpoint: bp.key, index: index, label: bp.label, columnConfig: columnConfig, setValue: setValue }, bp.key)))] }) }) })] }));
};
const ColumnLayout = ({ columns, spacing, backgroundImage, opacity, data, context, editData, configId, limitToPageWidth, }) => {
    const isEditing = editData && editData.activeConfigId === configId;
    const setValue = (breakpoint, columnId, newValue) => {
        const newColumns = updateColumns(columns, breakpoint, columnId, newValue);
        editData.actions.updateConfigValue(configId, 'props.columns', newColumns);
    };
    const deleteColumn = (columnId) => {
        navStore.showConfirmDialog('Confirm Delete', 'Are you sure you want to delete this column and its contents?', () => {
            const newColumns = clone(columns);
            remove(newColumns, (column) => (column.id === columnId));
            editData.actions.updateConfigValue(configId, 'props.columns', newColumns);
        }, 'Delete', undefined, 'Cancel');
    };
    const addColumn = () => {
        const newColumns = clone(columns);
        newColumns.push({
            id: v4(),
            xs: 12,
            sm: undefined,
            md: 6,
            lg: undefined,
            xl: undefined,
            config: null,
        });
        editData.actions.updateConfigValue(configId, 'props.columns', newColumns);
    };
    const isEmpty = () => (columns.length === 0 && editData);
    if (isEmpty()) {
        return (_jsx(PlaceHolder, { placeHolderName: "Configure Column Layout" }));
    }
    return (_jsxs(Box, { sx: {
            ...(limitToPageWidth && {
                width: '100%',
                maxWidth: 1200,
                marginX: 'auto',
            }),
        }, children: [isEditing && (_jsxs(Toolbar, { variant: "dense", sx: {
                    marginX: {
                        xs: 0,
                    },
                    paddingY: 1,
                    paddingX: {
                        xs: 1,
                    },
                    borderRadius: '2px',
                    backgroundColor: 'secondary.light',
                    borderTop: '1px solid grey',
                    borderBottom: '1px solid grey',
                    minHeight: 'auto',
                    opacity: 0.9,
                }, children: [`${columns.length} columns `, _jsx(Button, { variant: "outlined", onClick: addColumn, children: "Add Column" })] })), _jsx(BackgroundImage, { backgroundImage: backgroundImage, opacity: opacity, children: _jsx(Grid, { container: true, spacing: spacing, children: columns.map((column) => (_jsxs(Grid, { item: true, xs: column.xs, sm: column.sm, md: column.md, lg: column.lg, xl: column.xl, order: column.order, children: [isEditing && (_jsx(ColumnConfigEditor, { columnConfig: column, setValue: setValue, deleteColumn: deleteColumn })), column.config === null && editData ? (_jsx(editData.DropTarget, { dropId: `${configId}@${column.id}`, editData: editData, hoverToOpen: false, children: _jsx(PlaceHolder, { placeHolderName: "Configure Column" }) })) : (_jsx(Renderer, { configId: column.config, data: data, context: context, editData: editData }))] }, column.id))) }) })] }));
};
ColumnLayout.getData = async ({ columns, backgroundImage, data, context, editData, }, store) => (
// console.log('preloading ColumnLayout');
Promise.allSettled([
    BackgroundImage.getData({ backgroundImage, context }, store),
    ...columns.map((column) => (getData({
        configId: column.config,
        data,
        context,
        editData,
    }, store))),
]));
ColumnLayout.defaultProp = {
    spacing: 2,
};
const meta = {
    group: 'layouts',
    name: 'Column Layout',
    description: 'Builds responsive columns for placing components side by side.',
    props: [
        {
            key: 'spacing',
            name: 'Spacing',
            type: 'number',
            description: 'Spacing between columns.',
            group: 'config',
        },
        {
            key: 'limitToPageWidth',
            name: 'Limit Width',
            type: 'boolean',
            description: 'Limit the width of the stack to the maximum page width',
            group: 'style',
        },
        {
            key: 'layout',
            name: 'Layout',
            type: 'layout',
            description: 'Layout properties such as spacing and background color.',
            group: 'style',
        },
        {
            key: 'backgroundImage',
            name: 'Background Image',
            type: 'backgroundImage',
            description: 'Add a background image behind the columns.',
            group: 'config',
        },
        {
            key: 'opacity',
            name: 'Opacity',
            type: 'opacity',
            description: 'Change the Opacity of background.',
            group: 'style',
        },
    ],
    defaultConfig: {
        type: 'component',
        component: 'ColumnLayout',
        props: {
            spacing: 2,
            limitToPageWidth: true,
            opacity: '1',
            columns: [
                {
                    id: 'col1',
                    xs: 12,
                    md: 6,
                    config: null,
                },
                {
                    id: 'col2',
                    xs: 12,
                    md: 6,
                    config: null,
                },
            ],
        },
    },
    getChildren: (config) => (config.props.columns.map((column) => (column.config))),
};
ColumnLayout.meta = meta;
export default ColumnLayout;
//# sourceMappingURL=ColumnLayout.js.map